import React, { memo, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Animated, {
  Extrapolation,
  interpolate,
  type SharedValue,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from 'react-native-reanimated';
import { SvgIcon } from '../../components/common/SvgIcon';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import type { MenuFlowSection } from '../../data/domains/foodMenu';
import { useMenuFlowVariant } from '../../data/domains/config';

export const SECTION_HEADER_HEIGHT = 30;

/** Equal inset from top / trailing / bottom of the section bar. */
const CHECK_EDGE = 4;
const CHECK_SIZE = SECTION_HEADER_HEIGHT - CHECK_EDGE * 2;
const CHECK_SPRING = { stiffness: 420, damping: 28, mass: 0.4 };

export interface MenuFlowSectionHeaderProps {
  currentSection: MenuFlowSection | null;
  /**
   * set2: title sits in a surface chip on the primary header — use primary accent text.
   * default (set1/set3): parent row fills with `primary` when limit is reached — use onPrimary.
   */
  chrome_variant?: 'default' | 'set2';
  header_collapsed?: boolean;
  /**
   * set2 count presentation. List bars and sticky header both use `chip`.
   * Ignored when `count_blend` is provided (handoff morph).
   */
  count_chrome?: 'plain' | 'chip';
  /**
   * set2 handoff morph (optional). 0 / 1 blend between count chrome layers.
   */
  count_blend?: SharedValue<number>;
}

function createMenuFlowSectionHeaderStyles(c: ThemeColors) {
  const on_primary = c.onPrimary ?? c.textInverse ?? c.white;
  return StyleSheet.create({
    wrap: {
      width: '100%',
      height: SECTION_HEADER_HEIGHT,
      backfaceVisibility: 'hidden',
      backgroundColor: '#fff0',
      position: 'relative',
      overflow: 'hidden',
    },
    sectionHeaderTouchable: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingStart: 12,
      paddingEnd: 12,
      height: '100%',
      gap: 8,
    },
    sectionHeaderTouchableWithCheck: {
      paddingEnd: CHECK_EDGE + CHECK_SIZE + 8,
    },
    sectionHeaderText: {
      fontSize: 14,
      fontWeight: '500',
      color: c.text,
      letterSpacing: 1,
      flexShrink: 1,
    },
    sectionHeaderTextFull: {
      color: on_primary,
      fontWeight: '700',
    },
    sectionHeaderTextFullSet2: {
      color: c.primary,
      fontWeight: '700',
    },
    sectionHeaderCount: { fontSize: 14, color: c.textSecondary },
    sectionHeaderCountFull: {
      color: on_primary,
      fontWeight: '600',
    },
    sectionHeaderCountFullSet2: {
      color: c.primary,
      fontWeight: '600',
    },
    countSlot: {
      position: 'relative',
      justifyContent: 'center',
      alignItems: 'flex-end',
      flexShrink: 0,
      minWidth: 52,
      height: '100%',
    },
    countLayer: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'flex-end',
    },
    countChip: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 10,
      backgroundColor: c.surfaceLight,
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: c.borderLight,
      flexShrink: 0,
    },
    countEndRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexShrink: 0,
    },
    checkBadgeAnchor: {
      position: 'absolute',
      top: CHECK_EDGE,
      end: CHECK_EDGE,
      bottom: CHECK_EDGE,
      width: CHECK_SIZE,
      alignItems: 'center',
      justifyContent: 'center',
    },
    checkBadge: {
      width: CHECK_SIZE,
      height: CHECK_SIZE,
      borderRadius: CHECK_SIZE / 2,
      backgroundColor: c.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
  });
}

function Set2FullCheckBadge({
  visible,
  styles,
  checkColor,
}: {
  visible: boolean;
  styles: ReturnType<typeof createMenuFlowSectionHeaderStyles>;
  checkColor: string;
}) {
  const progress = useSharedValue(visible ? 1 : 0);

  useEffect(() => {
    progress.value = withSpring(visible ? 1 : 0, CHECK_SPRING);
  }, [visible, progress]);

  const anim = useAnimatedStyle(() => ({
    opacity: progress.value,
    transform: [{ scale: interpolate(progress.value, [0, 1], [0.55, 1], Extrapolation.CLAMP) }],
  }));

  return (
    <View style={styles.checkBadgeAnchor} pointerEvents="none">
      <Animated.View style={anim}>
        <View style={styles.checkBadge}>
          <SvgIcon name="check" size={12} color={checkColor} />
        </View>
      </Animated.View>
    </View>
  );
}

function Set2CountMorph({
  count_label,
  is_limit_reached,
  count_blend,
  styles,
}: {
  count_label: string;
  is_limit_reached: boolean;
  count_blend: SharedValue<number>;
  styles: ReturnType<typeof createMenuFlowSectionHeaderStyles>;
}) {
  const chip_style = useAnimatedStyle(() => ({
    opacity: interpolate(count_blend.value, [0, 0.55, 1], [1, 0.35, 0], Extrapolation.CLAMP),
    transform: [
      {
        scale: interpolate(count_blend.value, [0, 1], [1, 0.92], Extrapolation.CLAMP),
      },
    ],
  }));
  const plain_style = useAnimatedStyle(() => ({
    opacity: interpolate(count_blend.value, [0, 0.4, 1], [0, 0.55, 1], Extrapolation.CLAMP),
  }));

  return (
    <View style={styles.countSlot}>
      <Animated.View style={[styles.countLayer, chip_style]} pointerEvents="none">
        <View style={styles.countEndRow}>
          <View style={styles.countChip}>
            <Text
              style={[
                styles.sectionHeaderCount,
                is_limit_reached && styles.sectionHeaderCountFullSet2,
              ]}>
              {count_label}
            </Text>
          </View>
        </View>
      </Animated.View>
      <Animated.View style={[styles.countLayer, plain_style]} pointerEvents="none">
        <View style={styles.countEndRow}>
          <Text
            style={[
              styles.sectionHeaderCount,
              is_limit_reached && styles.sectionHeaderCountFullSet2,
            ]}>
            {count_label}
          </Text>
        </View>
      </Animated.View>
    </View>
  );
}

function Set2CountEnd({
  count_label,
  is_limit_reached,
  use_chip,
  styles,
}: {
  count_label: string;
  is_limit_reached: boolean;
  use_chip: boolean;
  styles: ReturnType<typeof createMenuFlowSectionHeaderStyles>;
}) {
  const count_node = use_chip ? (
    <View style={styles.countChip}>
      <Text
        style={[
          styles.sectionHeaderCount,
          is_limit_reached && styles.sectionHeaderCountFullSet2,
        ]}>
        {count_label}
      </Text>
    </View>
  ) : (
    <Text
      style={[
        styles.sectionHeaderCount,
        is_limit_reached && styles.sectionHeaderCountFullSet2,
      ]}>
      {count_label}
    </Text>
  );

  return <View style={styles.countEndRow}>{count_node}</View>;
}

export const MenuFlowSectionHeader = memo(({
  currentSection,
  chrome_variant = 'default',
  header_collapsed: _header_collapsed = false,
  count_chrome = 'plain',
  count_blend,
}: MenuFlowSectionHeaderProps) => {
  const menu_flow_variant = useMenuFlowVariant();
  const is_menu_flow_set2 = menu_flow_variant === 'set2';
  const section_header_height = is_menu_flow_set2 ? 38 : 30;
  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createMenuFlowSectionHeaderStyles(colors), [colors]);
  const check_color = colors.onPrimary ?? colors.textInverse ?? colors.white;

  if (!currentSection) {
    return null;
  }

  const is_limit_reached =
    currentSection.displayLimit > 0 && currentSection.selectedQuantity >= currentSection.displayLimit;
  const is_set2 = chrome_variant === 'set2';
  const count_label = `${currentSection.selectedQuantity} of ${currentSection.displayLimit}`;
  const use_chip = is_set2 && count_chrome === 'chip' && !count_blend;

  return (
    <View style={styles.wrap} pointerEvents="box-none">
      <View
        style={[
          styles.sectionHeaderTouchable,
          is_set2 && is_limit_reached && styles.sectionHeaderTouchableWithCheck,
        ]}>
        <Text
          style={[
            styles.sectionHeaderText,
            is_limit_reached && (is_set2 ? styles.sectionHeaderTextFullSet2 : styles.sectionHeaderTextFull),
          ]}
          numberOfLines={1}>
          {currentSection.title}
        </Text>
        {is_set2 && count_blend ? (
          <Set2CountMorph
            count_label={count_label}
            is_limit_reached={is_limit_reached}
            count_blend={count_blend}
            styles={styles}
          />
        ) : is_set2 ? (
          <Set2CountEnd
            count_label={count_label}
            is_limit_reached={is_limit_reached}
            use_chip={use_chip}
            styles={styles}
          />
        ) : (
          <Text
            style={[
              styles.sectionHeaderCount,
              is_limit_reached && styles.sectionHeaderCountFull,
            ]}>
            {count_label}
          </Text>
        )}
      </View>
      {is_set2 ? (
        <Set2FullCheckBadge visible={is_limit_reached} styles={styles} checkColor={check_color} />
      ) : null}
    </View>
  );
});
